import { createContext, useContext, type ReactNode } from "react";

// Legacy SVG vocabulary. The scoped R03 v08 surface uses the exact fetched
// Material Symbols Outlined font and glyph names from its Stitch export.
const glyphs = {
  overview: <><path d="m3 11 9-8 9 8M5 10v10h5v-6h4v6h5V10" /></>,
  project: <><path d="m3 7 9-5 9 5-9 5-9-5Zm0 5 9 5 9-5M3 17l9 5 9-5" /></>,
  datasets: <><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></>,
  groups: <><rect x="3" y="11" width="18" height="10" rx="2" /><path d="M5 11V8a1 1 0 0 1 1-1h12a1 1 0 0 1 1 1v3M8 7V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v3" /></>,
  views: <><path d="M4 5h16M4 10h16M4 15h10M4 20h10" /><path d="m17 16 3 2-3 2" /></>,
  upload: <><path d="M4 16v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3M12 16V3m-4 4 4-4 4 4" /></>,
  merge: <><path d="M5 4v3c0 3 3 5 7 5s7 2 7 5v3M19 4v3c0 3-3 5-7 5s-7 2-7 5v3m11-3 3 3 3-3" /></>,
  review: <><circle cx="12" cy="12" r="9" /><path d="m8 12 3 3 5-6" /></>,
  lineage: <><circle cx="6" cy="5" r="2" /><circle cx="18" cy="12" r="2" /><circle cx="6" cy="19" r="2" /><path d="M6 7v10m0-8c0 3 10 0 10 3" /></>,
  workflow: <><rect x="3" y="3" width="6" height="6" rx="1.5" /><rect x="15" y="15" width="6" height="6" rx="1.5" /><path d="M9 6h5a3 3 0 0 1 3 3v6m-3-3 3 3 3-3" /></>,
  factory: <><path d="M3 21V9l6 3V9l6 3V5h4v7l2-1v10H3Z" /><path d="M7 16h2m3 0h2m3 0h2" /></>,
  audit: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l4 2" /></>,
  delete: <><path d="M4 7h16M9 7V4h6v3M6 7l1 14h10l1-14M10 10v7m4-7v7" /></>,
  model: <><path d="m12 3 9 5v9l-9 5-9-5V8l9-5Zm0 10 9-5M3 8l9 5v9" /></>,
  training: <><circle cx="12" cy="12" r="8" /><path d="m10 8 6 4-6 4V8ZM5 3l-2 4 4 1m12 13 2-4-4-1" /></>,
  jobs: <><rect x="4" y="4" width="16" height="16" rx="3" /><path d="m10 8 6 4-6 4V8ZM8 1v3m8-3v3M8 20v3m8-3v3M1 8h3m-3 8h3m16-8h3m-3 8h3" /></>,
  settings: <><path d="M5 3v6m0 4v8m7-18v12m0 4v2m7-18v2m0 4v12" /><circle cx="5" cy="11" r="2" /><circle cx="12" cy="17" r="2" /><circle cx="19" cy="7" r="2" /></>,
  inbox: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
  account: <><circle cx="12" cy="8" r="4" /><path d="M4 21v-2a8 8 0 0 1 16 0v2" /></>,
  appearance: <><circle cx="12" cy="12" r="9" /><path d="M12 3a9 9 0 0 1 0 18Z" fill="currentColor" stroke="none" /></>,
  menu: <path d="M4 6h16M4 12h16M4 18h16" />,
  close: <path d="m6 6 12 12M18 6 6 18" />,
  chevron: <path d="m7 10 5 5 5-5" />,
  search: <><circle cx="10" cy="10" r="6" /><path d="m15 15 6 6" /></>,
  arrow: <path d="M4 12h16m-6-6 6 6-6 6" />,
  pause: <><path d="M8 5v14M16 5v14" strokeWidth="3" /></>,
  play: <path d="m8 5 11 7-11 7V5Z" />,
  check: <path d="m5 12 4 4L19 6" />,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v6m0-10v.01" /></>,
  file: <><path d="M13 3H5v18h14V9l-6-6Zm0 0v6h6M8 13h8m-8 4h5" /></>,
  folder: <path d="M3 7V4h6l2 3h10v13H3V7Z" />,
} satisfies Record<string, ReactNode>;

export type IconName = keyof typeof glyphs;
export type MaterialGlyph = "verified" | "sync" | "policy" | "inventory_2" | "autorenew" | "schedule" | "help_outline" | "info"
  | "cloud_upload" | "expand_more" | "folder_open" | "archive" | "add_circle"
  | "upload_file" | "summarize" | "verified_user" | "folder"
  | "tune" | "model_training" | "fact_check" | "science" | "lock" | "dataset" | "add_box" | "input"
  | "note_add" | "create_new_folder" | "description" | "playlist_add_check" | "label" | "data_usage" | "rate_review";
export const IconFamily = createContext<"legacy" | "material">("legacy");

/** Any Material Symbols Outlined glyph by name, from the pinned shell font. Decorative: the text beside it carries
 *  the meaning. Feature pages use this one component instead of a local glyph span (`.ui-glyph` in styles.css). */
export function Glyph({ name, size = 16, className = "" }: { name: string; size?: number; className?: string }) {
  return <span aria-hidden="true" className={className ? `ui-glyph ${className}` : "ui-glyph"} style={{ fontSize: size }}>{name}</span>;
}

/** A quiet, decorative icon surface; the adjacent label always carries its meaning. */
export function IconBadge({ name, tone = "neutral" }: { name: string; tone?: "neutral" | "accent" | "danger" }) {
  return <span aria-hidden="true" className="celia-icon-badge" data-tone={tone}><Glyph name={name} size={18} /></span>;
}

/** Glyphs named by the installed Material Symbols Outlined font. */
export function MaterialSymbol({ name, size = 18, className = "" }: { name: MaterialGlyph; size?: number; className?: string }) {
  return <span aria-hidden="true" className={`design-icon ${className}`} style={{ fontSize: size }}>{name}</span>;
}

const designGlyphs: Record<IconName, string> = {
  overview: "dashboard", project: "apps", datasets: "dataset", groups: "folder_copy",
  views: "filter_alt", upload: "cloud_upload", merge: "merge", review: "rate_review",
  lineage: "account_tree", workflow: "schema", factory: "precision_manufacturing", audit: "history", delete: "delete_forever",
  model: "deployed_code", training: "model_training", jobs: "work_history", settings: "settings", inbox: "inbox",
  account: "person", appearance: "brightness_medium", menu: "menu", close: "close",
  chevron: "expand_more", search: "search", arrow: "arrow_forward", check: "check",
  pause: "pause", play: "play_arrow",
  info: "info", file: "description", folder: "folder",
};

export function Icon({ name, size = 18, className = "" }: { name: IconName; size?: number; className?: string }) {
  if (useContext(IconFamily) === "material") {
    return <span aria-hidden="true" className={`design-icon ${className}`} style={{ fontSize: size }}>{designGlyphs[name]}</span>;
  }
  return <svg aria-hidden="true" focusable="false" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="1.65" strokeLinecap="round" strokeLinejoin="round" className={`shrink-0 ${className}`}>{glyphs[name]}</svg>;
}

export function CeliaMark({ size = 24 }: { size?: number }) {
  return (
    <span className="celia-mark" style={{ width: size, height: size }}>
      <img src="/celia-mark.svg" alt="" draggable={false} />
    </span>
  );
}
